import { color, snap, units } from '@m/alchemy-ui/theme';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { ConfirmCheckmarkIcon } from '@m/alchemy-ui/icons/ConfirmCheckmarkIcon';
import { EditIcon } from '@m/alchemy-ui/icons/EditIcon';
import { IconButton } from '@m/alchemy-ui/IconButton';
import { Popper } from '@m/alchemy-ui/Popper';
import { PopperBox } from '@m/alchemy-ui/PopperBox';
import { PortalRoot } from '@m/alchemy-ui/PortalRoot';

import { ThumbsUpIcon } from '@m/alchemy-ui/icons/ThumbsUpIcon';

import { useFormikContext } from 'formik';
import { type IntlShape, useIntl } from 'react-intl';
import { type IChallenge } from '../../../common/interfaces';
import { type Milestone, MilestoneType } from '../../../common/core-api';
import { RocketIcon } from '../../../common/icons';
import { useChallenge } from '../context/Challenge';
import { MilestoneForm } from '.';

const MilestoneFormPopper = styled(PopperBox)`
  width: ${snap(1000)};
`;

const MilestoneIndicatorWrapper = styled(IconButton)`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${color('lineContrast')};
  border-radius: 50%;
  width: ${units(5)};
  height: ${units(5)};
  svg {
    color: ${color('inverse')};
    width: ${units(2.5)};
    height: ${units(2.5)};
  }
  cursor: pointer;
`;

function getIconByType(type: MilestoneType | null | undefined, intl: IntlShape) {
  switch (type) {
    case MilestoneType.SUBMITTED:
      return (
        <RocketIcon
          aria-label={intl.formatMessage({
            id: 'editSubmittedPhase',
            defaultMessage: 'Edit submitted phase'
          })}
        />
      );
    case MilestoneType.APPROVAL:
      return (
        <ConfirmCheckmarkIcon
          aria-label={intl.formatMessage({
            id: 'editApprovalPhase',
            defaultMessage: 'Edit approval phase'
          })}
        />
      );
    case MilestoneType.VOTE:
      return (
        <ThumbsUpIcon
          aria-label={intl.formatMessage({
            id: 'editVotePhase',
            defaultMessage: 'Edit vote phase'
          })}
        />
      );
    default:
      return (
        <EditIcon
          aria-label={intl.formatMessage({
            id: 'editPhase',
            defaultMessage: 'Edit Phase'
          })}
        />
      );
  }
}

export const MilestoneIndicator = ({
  milestone,
  milestoneIndex
}: {
  readonly milestone: Milestone;
  readonly milestoneIndex: number;
}) => {
  const { type } = milestone;
  const { challenge } = useChallenge();
  const { status } = useFormikContext<IChallenge>();

  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);

  const hasError =
    challenge.focusError?.name?.includes('milestone.milestones') && challenge.focusError?.itemIndex === milestoneIndex;

  const handleMilestoneClick = (value = false) => {
    const popupsArray = document.querySelectorAll('.milestonePopper');
    const body = document.querySelector('#fullContainer') as HTMLElement;

    if (body && value) {
      /**
       * 1000px max default height of popup
       */
      body.style.paddingBottom = '1000px';
    } else if (body && !value && popupsArray.length < 2) {
      body.style.paddingBottom = '0';
    }

    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (hasError && !status?.saving) {
      setIsOpen(hasError);
    }
  }, [hasError, status?.saving]);

  return (
    <Popper
      shouldCloseOnOutsideInteraction={false}
      placement="bottom"
      isOpen={isOpen}
      trigger={
        <MilestoneIndicatorWrapper icon={getIconByType(type, intl)} onClick={() => handleMilestoneClick(true)} />
      }
      overlay={
        <div className="milestonePopper">
          <PortalRoot>
            <MilestoneFormPopper
              hasArrow
              hasPadding
              maxWidth={snap(1000)}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
              }}
            >
              <MilestoneForm
                setPopperOpen={handleMilestoneClick}
                currentMilestone={milestone}
                currentMilestoneIndex={milestoneIndex}
              />
            </MilestoneFormPopper>
          </PortalRoot>
        </div>
      }
    />
  );
};
